<template>
    <div>
        <div v-if="!isLoading" class="fade-in p-3">
            <div class="row">
                <div class="col-12">
                    <div class="d-flex justify-content-end mt-0 mb-1">
                        <b-spinner v-if="isSequenceLoading" variant="info" small />
                    </div>
                    <draggable
                        v-model="tableRows"
                        handle=".department-drag-handle"
                        ghost-class="drag-ghost"
                        chosen-class="drag-chosen"
                        drag-class="drag-active"
                        :animation="300"
                        easing="cubic-bezier(0.25, 1, 0.5, 1)"
                        :disabled="!status.save || isSequenceLoading"
                        @end="updateSequenceRequest"
                    >
                        <div v-for="item in tableRows" :key="item.uuid" class="border rounded bg-white mb-3 overflow-hidden shadow-sm">
                            <div class="row no-gutters">
                                <div class="col-lg-3 bg-light border-right p-2">
                                    <div class="d-flex align-items-center mb-3">
                                        <!-- 1. Drag Handle -->
                                        <div class="flex-shrink-0 mr-3">
                                            <i class="mdi mdi-menu-swap font-size-18 cursor-pointer department-drag-handle text-muted"></i>
                                        </div>

                                        <!-- 2. Department Name -->
                                        <div class="flex-grow-1 min-width-0">
                                            <h6 class="font-weight-bold font-size-13 mb-0 text-break">
                                                {{ item.department ? item.department.name : '--' }}
                                            </h6>
                                        </div>

                                        <!-- 3. Action Buttons -->
                                        <div class="d-flex align-items-center flex-shrink-0 ml-3">
                                            <b-button
                                                type="button"
                                                variant="link"
                                                class="text-info p-0"
                                                :disabled="!status.save || isSequenceLoading"
                                                :title="$t('label.updateX', [])"
                                                v-b-tooltip.hover="{ boundary: 'window' }"
                                                @click="openForm(item)"
                                            >
                                                <i class="mdi mdi-pencil font-size-16"></i>
                                            </b-button>

                                            <b-button
                                                type="button"
                                                variant="link"
                                                class="text-danger p-0"
                                                :disabled="!status.save || isSequenceLoading"
                                                :title="$t('label.deleteX', [])"
                                                v-b-tooltip.hover="{ boundary: 'window' }"
                                                @click="deleteConfirmation(item)"
                                            >
                                                <i class="mdi mdi-delete-outline font-size-16"></i>
                                            </b-button>

                                            <b-button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                class="p-0 text-info"
                                                :title="$t('label.viewX', [$t('label.contact')])"
                                                v-b-tooltip.hover="{ boundary: 'window' }"
                                                :disabled="!status.save || isSequenceLoading"
                                                @click="toggleDepartment(item.uuid)"
                                            >
                                                <i :class="['mdi mr-1', expandedDepartments.includes(item.uuid) ? 'mdi-chevron-down' : 'mdi-chevron-right']"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-lg-9">
                                    <PhoneExtItemSortable v-if="expandedDepartments.includes(item.uuid)" :departmentUUID="item.uuid" />
                                    <div v-else class="d-flex align-items-center justify-content-center h-100 p-4 text-muted small">
                                        <i class="mdi mdi-phone-outline mr-2"></i>Select View contacts to see this department's directory.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </draggable>
                    <b-button type="button" variant="outline-info" size="sm" class="w-100 border-dashed font-size-10 font-weight-bolder" :disabled="!status.save || isSequenceLoading" @click="openForm()">
                        <i class="mdi mdi-plus-circle-outline mr-2"></i>{{ $t('label.addX', [$t('module.department')]) }}
                    </b-button>
                </div>
            </div>
        </div>
        <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
            <b-spinner class="m-5" variant="info" role="status" />
        </div>
        <b-modal
            :id="formModalId"
            :title="$t(departmentItem.uuid ? 'label.updateX' : 'label.createX', [$t('module.department')])"
            @hidden="closeForm"
            hide-footer
            hide-header-close
            centered
            size="lg"
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
        >
            <b-container fluid>
                <PhoneExtDepartmentForm
                    v-if="showForm"
                    :groupUUID="groupUUID"
                    :departmentUUID="departmentItem.uuid"
                    :sequence="departmentItem.sequence"
                    @done="closeForm"
                    @reload="getDepartmentList"
                />
            </b-container>
        </b-modal>
        <b-modal :id="deleteModalId" :title="$t('label.deleteX', [$t('module.department')])" hide-header-close :no-close-on-backdrop="!status.save" :no-close-on-esc="!status.save">
            <p class="my-4">{{ $t('message.deleteConfirmation') }}</p>
            <template #modal-footer>
                <b-button variant="danger" :disabled="!status.save" @click="$bvModal.hide(deleteModalId)">{{ $t('label.cancel') }}</b-button>
                <b-button variant="info" :disabled="!status.save" @click="requestDelete">
                    <span v-if="status.save">{{ $t('label.deleteX', []) }}</span>
                    <b-spinner v-else small />
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import PhoneExtItemSortable from '@/views/pages/admin/phoneExtension/phoneExtensionItem/phoneExtItemSortable';
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import { phoneExtDepartmentMethods } from '@/state/api';
import { PHONE_EXT_DEPARTMENT_MODULE } from '@/state/api/phoneExtDepartment.state';
import PhoneExtDepartmentForm from '@/views/pages/admin/phoneExtension/phoneExtensionDepartment/phoneExtDepartmentForm';

export default {
    props: {
        groupUUID: { type: String, required: true },
    },
    components: { draggable, PhoneExtDepartmentForm, PhoneExtItemSortable },
    created() {
        this.getDepartmentList();
    },
    data() {
        return {
            isLoading: true,
            isSequenceLoading: false,
            showForm: false,
            expandedDepartments: [],
            departmentItem: { uuid: '', sequence: 1 },
        };
    },
    methods: {
        ...phoneExtDepartmentMethods,
        toggleDepartment(uuid) {
            const index = this.expandedDepartments.indexOf(uuid);
            if (index == -1) this.expandedDepartments.push(uuid);
            else this.expandedDepartments.splice(index, 1);
        },
        getDepartmentList() {
            this.isLoading = true;
            return this.getAllPhoneExtDepartment({filter: {phoneExtGroupUuid: this.groupUUID, isActive: 1}})
                .then((result) => {
                    this.isLoading = false;
                    return result;
                });
        },
        updateSequenceRequest(event) {
            if (event.oldIndex == event.newIndex) return;
            this.isSequenceLoading = true;
            let data = {
                phoneExtGroupUuid: this.groupUUID,
                departments: this.tableRows.map((item, index) => ({uuid: item.uuid, sequence: index + 1})),
            };
            return this.updatePhoneExtDepartmentSequence({data})
                .then(() => this.getDepartmentList())
                .then(() => { this.isSequenceLoading = false; });
        },
        openForm(item) {
            if (!this.status.save || this.isSequenceLoading) return;
            this.departmentItem = item?.uuid ? {uuid: item.uuid, sequence: item.sequence} : {
                uuid: '',
                sequence: Math.max(0, ...this.tableRows.map(row => Number(row.sequence) || 0)) + 1,
            };
            this.showForm = true;
            this.$nextTick(() => this.$bvModal.show(this.formModalId));
        },
        closeForm() {
            this.showForm = false;
            this.departmentItem = { uuid: '', sequence: 1 };
            this.$bvModal.hide(this.formModalId);
        },
        deleteConfirmation(item) {
            this.departmentItem = {uuid: item.uuid, sequence: item.sequence};
            this.$bvModal.show(this.deleteModalId);
        },
        requestDelete() {
            if (!this.status.save) return;
            let data = {uuid: this.departmentItem.uuid, isActive: 0};
            return this.updatePhoneExtDepartmentStatus({data})
                .then((result) => {
                    if (result?.success) {
                        this.$bvModal.hide(this.deleteModalId);
                        return this.getDepartmentList();
                    }
                });
        },
    },
    computed: {
        ...mapState(PHONE_EXT_DEPARTMENT_MODULE, ['status']),
        formModalId() {
            return `phone-ext-department-form-${this.groupUUID}`;
        },
        deleteModalId() {
            return `phone-ext-department-delete-${this.groupUUID}`;
        },
        tableRows: {
            get() {
                return (this.$store.state.api.phoneExtDepartment.listByGroup[this.groupUUID] || [])
                    .map(item => ({...item}));
            },
            set(value) {
                this.$store.commit(`${PHONE_EXT_DEPARTMENT_MODULE}/setPhoneExtDepartmentByGroup`, {
                    phoneExtGroupUuid: this.groupUUID,
                    data: value.map((item, index) => ({...item, sequence: index + 1})),
                });
            },
        },
    },
};
</script>
<style scoped>
.department-drag-handle {
    cursor: grab;
}
.border-dashed {
    border-style: dashed !important;
}
</style>
